import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

interface UseFeedPagingParams {
  itemCount: number;
  initialIndex: number;
}

interface UseFeedPagingResult {
  scrollRef: React.RefObject<HTMLDivElement>;
  setSlideRef: (index: number) => (el: HTMLDivElement | null) => void;
  activeIndex: number;
  hasUserScrolled: boolean;
}

const ACTIVE_SLIDE_THRESHOLD = 0.6;

const useFeedPaging = ({
  itemCount,
  initialIndex,
}: UseFeedPagingParams): UseFeedPagingResult => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);

  const slideRefCallbacks = useRef(
    new Map<number, (el: HTMLDivElement | null) => void>()
  );

  const setSlideRef = useCallback((index: number) => {
    let callback = slideRefCallbacks.current.get(index);
    if (!callback) {
      callback = (el: HTMLDivElement | null) => {
        slideRefs.current[index] = el;
      };
      slideRefCallbacks.current.set(index, callback);
    }
    return callback;
  }, []);

  // Pin the feed to initialIndex until the user scrolls. Two things make the
  // naive "scrollIntoView on mount" fail:
  //  1. initialIndex can change a frame or two after mount (activeTabSlug /
  //     entrySlug / sections settle across renders), so we re-run on change
  //     rather than latching once.
  //  2. The DS Overlay lays its content out a tick after mount, so on the first
  //     layout pass the scroll container reports clientHeight: 0 and
  //     scrollIntoView is a no-op that leaves the feed on slide 0. We wait for
  //     the container to get a real height (ResizeObserver) before scrolling.
  // scrollIntoView fires no touchmove/wheel, so it never trips hasUserScrolled.
  useLayoutEffect(() => {
    if (hasUserScrolled || itemCount === 0) return undefined;
    const root = scrollRef.current;
    const target = slideRefs.current[initialIndex];
    if (!root || !target) return undefined;

    const tryScroll = (): boolean => {
      if (hasUserScrolled || root.clientHeight === 0) return false;
      target.scrollIntoView();
      setActiveIndex(initialIndex);
      return true;
    };

    if (tryScroll()) return undefined;

    const observer = new ResizeObserver(() => {
      if (tryScroll()) observer.disconnect();
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, [itemCount, initialIndex, hasUserScrolled]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || itemCount === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(
              (entry.target as HTMLElement).dataset.index ?? '0'
            );
            setActiveIndex(index);
          }
        });
      },
      { root, threshold: ACTIVE_SLIDE_THRESHOLD }
    );
    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [itemCount]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || itemCount === 0) return undefined;
    const markUserScrolled = () => setHasUserScrolled(true);
    root.addEventListener('touchmove', markUserScrolled, { passive: true });
    root.addEventListener('wheel', markUserScrolled, { passive: true });
    return () => {
      root.removeEventListener('touchmove', markUserScrolled);
      root.removeEventListener('wheel', markUserScrolled);
    };
  }, [itemCount]);

  return { scrollRef, setSlideRef, activeIndex, hasUserScrolled };
};

export default useFeedPaging;
